import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Target,
  Plus,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileEdit,
  TrendingUp,
  Calendar,
  User,
  FileText,
} from "lucide-react";
import { GoalCard } from "./GoalCard";
import { AddGoalDialog } from "./AddGoalDialog";
import { AddNoteDialog } from "./AddNoteDialog";

interface CarePlan {
  id: string;
  member_id: string;
  provider_id: string;
  title: string;
  description: string | null;
  status: "draft" | "active" | "completed" | "discontinued";
  start_date: string;
  target_end_date: string | null;
  actual_end_date: string | null;
  diagnosis: string | null;
  created_at: string;
  updated_at: string;
  member_name?: string;
}

interface CarePlanDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  carePlan: CarePlan;
  onUpdate: () => void;
}

const statusConfig = {
  draft: { label: "Draft", color: "bg-muted text-muted-foreground" },
  active: { label: "Active", color: "bg-success/10 text-success" },
  completed: { label: "Completed", color: "bg-primary/10 text-primary" },
  discontinued: { label: "Discontinued", color: "bg-destructive/10 text-destructive" },
};

export function CarePlanDetailSheet({
  open,
  onOpenChange,
  carePlan,
  onUpdate,
}: CarePlanDetailSheetProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [addGoalOpen, setAddGoalOpen] = useState(false);
  const [addNoteOpen, setAddNoteOpen] = useState(false);

  // Fetch goals
  const { data: goals, refetch: refetchGoals } = useQuery({
    queryKey: ["care-plan-goals", carePlan.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("care_plan_goals")
        .select("*")
        .eq("care_plan_id", carePlan.id)
        .order("priority", { ascending: false });
      if (error) throw error;
      return data as Array<{
        id: string;
        care_plan_id: string;
        title: string;
        description: string | null;
        target_value: number | null;
        current_value: number | null;
        unit: string | null;
        priority: "low" | "medium" | "high";
        status: "not_started" | "in_progress" | "achieved" | "discontinued";
        target_date: string | null;
        created_at: string;
      }>;
    },
    enabled: open,
  });

  // Fetch notes
  const { data: notes, refetch: refetchNotes } = useQuery({
    queryKey: ["care-plan-notes", carePlan.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("care_plan_notes")
        .select("*")
        .eq("care_plan_id", carePlan.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      const updateData: Record<string, unknown> = { status: newStatus };
      if (newStatus === "completed") {
        updateData.actual_end_date = format(new Date(), "yyyy-MM-dd");
      }
      const { error } = await supabase
        .from("care_plans")
        .update(updateData)
        .eq("id", carePlan.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Status updated" });
      onUpdate();
    },
    onError: (error) => {
      toast({ title: "Error updating status", description: error.message, variant: "destructive" });
    },
  });

  const goalsCompleted = goals?.filter(g => g.status === "achieved").length || 0;
  const goalsTotal = goals?.length || 0;
  const progress = goalsTotal > 0 ? Math.round((goalsCompleted / goalsTotal) * 100) : 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="mb-6">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-xl">{carePlan.title}</SheetTitle>
            <Badge className={statusConfig[carePlan.status].color}>
              {statusConfig[carePlan.status].label}
            </Badge>
          </div>
          <SheetDescription className="flex items-center gap-2">
            <User className="h-4 w-4" />
            {carePlan.member_name}
          </SheetDescription>
        </SheetHeader>

        {/* Overview */}
        <div className="space-y-4 mb-6">
          {carePlan.diagnosis && (
            <div>
              <p className="text-sm text-muted-foreground">Diagnosis</p>
              <p className="font-medium">{carePlan.diagnosis}</p>
            </div>
          )}

          {carePlan.description && (
            <div>
              <p className="text-sm text-muted-foreground">Description</p>
              <p className="text-sm">{carePlan.description}</p>
            </div>
          )}

          <div className="flex gap-4 text-sm">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Calendar className="h-4 w-4" />
              Started {format(new Date(carePlan.start_date), "MMM d, yyyy")}
            </div>
            {carePlan.target_end_date && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <Target className="h-4 w-4" />
                Target {format(new Date(carePlan.target_end_date), "MMM d, yyyy")}
              </div>
            )}
          </div>

          {/* Progress */}
          <div className="rounded-lg bg-muted/50 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Overall Progress</span>
              <span className="text-sm text-muted-foreground">{goalsCompleted}/{goalsTotal} goals</span>
            </div>
            <Progress value={progress} className="h-3" />
            <p className="mt-2 text-xs text-muted-foreground">{progress}% complete</p>
          </div>

          {/* Status Actions */}
          {carePlan.status === "active" && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => updateStatusMutation.mutate("completed")}
                disabled={updateStatusMutation.isPending}
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Mark Complete
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1 text-destructive"
                onClick={() => updateStatusMutation.mutate("discontinued")}
                disabled={updateStatusMutation.isPending}
              >
                <AlertCircle className="mr-2 h-4 w-4" />
                Discontinue
              </Button>
            </div>
          )}
          {carePlan.status === "draft" && (
            <Button
              className="w-full"
              onClick={() => updateStatusMutation.mutate("active")}
              disabled={updateStatusMutation.isPending}
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Activate Plan
            </Button>
          )}
        </div>

        <Separator className="my-6" />

        {/* Tabs for Goals and Notes */}
        <Tabs defaultValue="goals">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="goals" className="gap-2">
              <Target className="h-4 w-4" />
              Goals ({goalsTotal})
            </TabsTrigger>
            <TabsTrigger value="notes" className="gap-2">
              <FileText className="h-4 w-4" />
              Notes ({notes?.length || 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="goals" className="mt-4 space-y-4">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setAddGoalOpen(true)}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Goal
            </Button>

            {goals?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No goals added yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {goals?.map(goal => (
                  <GoalCard
                    key={goal.id}
                    goal={goal}
                    onUpdate={() => {
                      refetchGoals();
                      onUpdate();
                    }}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="notes" className="mt-4 space-y-4">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setAddNoteOpen(true)}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Progress Note
            </Button>

            {notes?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No notes yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notes?.map(note => (
                  <div key={note.id} className="rounded-lg border p-4">
                    <p className="text-sm">{note.note}</p>
                    <p className="mt-2 text-xs text-muted-foreground">
                      {format(new Date(note.created_at), "MMM d, yyyy 'at' h:mm a")}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Add Goal Dialog */}
        <AddGoalDialog
          open={addGoalOpen}
          onOpenChange={setAddGoalOpen}
          carePlanId={carePlan.id}
          onSuccess={() => {
            refetchGoals();
            onUpdate();
          }}
        />

        {/* Add Note Dialog */}
        <AddNoteDialog
          open={addNoteOpen}
          onOpenChange={setAddNoteOpen}
          carePlanId={carePlan.id}
          goals={goals || []}
          onSuccess={() => refetchNotes()}
        />
      </SheetContent>
    </Sheet>
  );
}
